import {
  ReaChangeDigestItem,
  ReaFieldChange,
  ReaImportChange,
  ReaImportRichSummary,
  ReaImportOmittedDetail,
  ReaMetricItem,
  ReaReviewReasonItem,
} from 'src/app/models/fincas/rea.model';

/**
 * Splits REA import counters into farmer-facing and support-facing summaries.
 */
export class ReaImportSummaryPresenter {
  public static customerMetrics(summary: ReaImportRichSummary | null): ReaMetricItem[] {
    if (summary == null) {
      return [];
    }

    const parcelasParaRevisar = this.reviewRequiredCount(summary);

    return [
      { label: 'Parcelas nuevas', value: summary.parcelasInsertadas, visible: summary.parcelasInsertadas > 0 },
      { label: 'Parcelas actualizadas', value: summary.parcelasActualizadas, visible: summary.parcelasActualizadas > 0 },
      { label: 'Parcelas sin cambios', value: summary.parcelasSinCambios, visible: summary.parcelasSinCambios > 0 },
      { label: 'Parcelas para revisar', value: parcelasParaRevisar, visible: parcelasParaRevisar > 0 },
      {
        label: 'Superficie detectada',
        value: `${summary.superficieTotalDetectada} ha`,
        visible: summary.superficieTotalDetectada > 0,
      },
    ];
  }


  public static reviewRequiredCount(summary: ReaImportRichSummary | null): number {
    if (summary == null) {
      return 0;
    }

    const omittedDetail = this.omittedDetail(summary);
    const invalidGeometriesOutsideOmitted = Math.max(
      0,
      (summary.geometriasInvalidas ?? 0) - omittedDetail.wktInvalido,
    );

    return summary.parcelasOmitidas
      + (summary.parcelasDesaparecidasRea ?? 0)
      + invalidGeometriesOutsideOmitted;
  }

  public static reviewReasons(summary: ReaImportRichSummary | null): ReaReviewReasonItem[] {
    if (summary == null) {
      return [];
    }

    const reasons: ReaReviewReasonItem[] = [];
    const omittedDetail = this.omittedDetail(summary);

    if (summary.parcelasOmitidas > 0) {
      reasons.push({
        key: 'omitted',
        count: summary.parcelasOmitidas,
        label: this.labelWithCount(
          summary.parcelasOmitidas,
          'parcela requiere revisión manual',
          'parcelas requieren revisión manual',
        ),
        description: this.omittedReasonDescription(summary.parcelasOmitidas, omittedDetail),
        recommendedAction: summary.parcelasOmitidas === 1
          ? 'Revisa esa parcela y decide si debe añadirse, corregirse o dejarse fuera.'
          : 'Revisa esas parcelas y decide cuáles deben añadirse, corregirse o dejarse fuera.',
      });
    }

    const invalidGeometriesOutsideOmitted = Math.max(
      0,
      (summary.geometriasInvalidas ?? 0) - omittedDetail.wktInvalido,
    );

    if (invalidGeometriesOutsideOmitted > 0) {
      reasons.push({
        key: 'invalid-geometry',
        count: invalidGeometriesOutsideOmitted,
        label: this.labelWithCount(
          invalidGeometriesOutsideOmitted,
          'parcela tiene un dibujo que no se puede usar automáticamente',
          'parcelas tienen un dibujo que no se puede usar automáticamente',
        ),
        description: 'El contorno oficial necesita revisión antes de poder sincronizarse correctamente.',
        recommendedAction: 'Revisa el dibujo en el mapa y corrige la geometría si no coincide con la parcela real.',
      });
    }

    if ((summary.parcelasDesaparecidasRea ?? 0) > 0) {
      const count = summary.parcelasDesaparecidasRea ?? 0;

      reasons.push({
        key: 'missing-in-rea',
        count,
        label: this.labelWithCount(count, 'parcela no aparece en el REA actual', 'parcelas no aparecen en el REA actual'),
        description: 'Conviene revisar si siguen vigentes o si deben mantenerse como histórico en tu explotación.',
        recommendedAction: 'Comprueba la parcela en la finca y decide si debe mantenerse, actualizarse o marcarse como no vigente.',
      });
    }

    return reasons;
  }

  public static technicalMetrics(summary: ReaImportRichSummary | null): ReaMetricItem[] {
    if (summary == null) {
      return [];
    }

    const synchronizedGeometries = summary.geometriasInsertadas + summary.geometriasActualizadas;

    return [
      { label: 'Fincas detectadas', value: summary.fincasDetectadas, visible: true },
      { label: 'Parcelas detectadas', value: summary.parcelasDetectadas, visible: true },
      { label: 'Parcelas omitidas', value: summary.parcelasOmitidas, visible: true },
      {
        label: 'Geometrías detectadas',
        value: summary.geometriasDetectadas ?? 0,
        visible: (summary.geometriasDetectadas ?? 0) > 0,
      },
      { label: 'Geometrías sincronizadas', value: synchronizedGeometries, visible: true },
      { label: 'WKT inválidos', value: summary.geometriasInvalidas ?? 0, visible: (summary.geometriasInvalidas ?? 0) > 0 },
      {
        label: 'Multipolígonos',
        value: summary.geometriasMultipoligono ?? 0,
        visible: (summary.geometriasMultipoligono ?? 0) > 0,
      },
      { label: 'Cultivos identificados', value: summary.cultivosIdentificados, visible: true },
      {
        label: 'Cultivos sin identificar',
        value: summary.cultivosNoIdentificados,
        visible: summary.cultivosNoIdentificados > 0,
      },
      {
        label: 'Cambios detectados',
        value: summary.parcelasConCambios ?? 0,
        visible: (summary.parcelasConCambios ?? 0) > 0,
      },
      {
        label: 'Cambio superficie',
        value: summary.parcelasConCambioSuperficie ?? 0,
        visible: (summary.parcelasConCambioSuperficie ?? 0) > 0,
      },
      {
        label: 'Cambio cultivo',
        value: summary.parcelasConCambioCultivo ?? 0,
        visible: (summary.parcelasConCambioCultivo ?? 0) > 0,
      },
      {
        label: 'Cambio geometría',
        value: summary.parcelasConCambioGeometria ?? 0,
        visible: (summary.parcelasConCambioGeometria ?? 0) > 0,
      },
      {
        label: 'No aparecen en REA',
        value: summary.parcelasDesaparecidasRea ?? 0,
        visible: (summary.parcelasDesaparecidasRea ?? 0) > 0,
      },
      { label: 'Maquinaria importada', value: summary.maquinariaImportada, visible: summary.maquinariaImportada > 0 },
      { label: 'Histórico guardado', value: summary.historicoCreado ? 'Sí' : 'No', visible: true },
    ];
  }

  public static changeDigest(changes: ReaImportChange[]): ReaChangeDigestItem[] {
    const inserted = this.countChangesByAction(changes, 'inserted');
    const updated = this.countChangesByAction(changes, 'updated');
    const missingInRea = this.countChangesByAction(changes, 'not_in_rea');
    const geometryChanges = this.countChangesByField(changes, 'geometria');

    return [
      { label: 'Nuevas', value: inserted, visible: inserted > 0 },
      { label: 'Actualizadas', value: updated, visible: updated > 0 },
      { label: 'No aparecen en REA', value: missingInRea, visible: missingInRea > 0 },
      { label: 'Con cambio de geometría', value: geometryChanges, visible: geometryChanges > 0 },
    ];
  }


  private static omittedDetail(summary: ReaImportRichSummary): ReaImportOmittedDetail {
    return summary.parcelasOmitidasDetalle ?? { porFechaBaja: 0, sinCultivos: 0, sinGeometria: 0, wktInvalido: 0 };
  }

  private static omittedReasonDescription(parcelCount: number, detail: ReaImportOmittedDetail): string {
    const reasons = this.omittedReasonNames(detail);

    if (reasons.length === 0) {
      return parcelCount === 1
        ? 'La parcela necesita revisión manual antes de poder incorporarla correctamente.'
        : 'Estas parcelas necesitan revisión manual antes de poder incorporarlas correctamente.';
    }

    const reasonText = this.joinFragments(reasons);

    return parcelCount === 1
      ? `La parcela necesita revisión porque ${reasonText}.`
      : `Las parcelas necesitan revisión porque ${reasonText}.`;
  }

  private static omittedReasonNames(detail: ReaImportOmittedDetail): string[] {
    const reasons: string[] = [];

    if (detail.porFechaBaja > 0) {
      reasons.push('figuran dadas de baja');
    }

    if (detail.sinCultivos > 0) {
      reasons.push('falta el cultivo');
    }

    if (detail.sinGeometria > 0) {
      reasons.push('falta el dibujo de la parcela');
    }

    if (detail.wktInvalido > 0) {
      reasons.push('el dibujo no se puede usar automáticamente');
    }

    return reasons;
  }

  private static labelWithCount(count: number, singular: string, plural: string): string {
    return `${count} ${count === 1 ? singular : plural}`;
  }

  private static joinFragments(fragments: string[]): string {
    if (fragments.length === 1) {
      return fragments[0] ?? '';
    }

    if (fragments.length === 2) {
      return `${fragments[0]} y ${fragments[1]}`;
    }

    return `${fragments.slice(0, -1).join(', ')} y ${fragments[fragments.length - 1]}`;
  }

  private static countChangesByAction(changes: ReaImportChange[], action: string): number {
    return changes.filter((change) => change.action === action).length;
  }

  private static countChangesByField(changes: ReaImportChange[], field: string): number {
    return changes.filter((change) => this.fieldChangesFor(change).some((fieldChange) => fieldChange.field === field)).length;
  }

  private static fieldChangesFor(change: ReaImportChange): ReaFieldChange[] {
    return Array.isArray(change.fieldChanges) ? change.fieldChanges : [];
  }
}
